import {
  format as dateFnsFormat,
  differenceInDays,
  isAfter,
  isSameDay,
  max,
  parseISO,
  set,
  isToday,
  isTomorrow,
  differenceInMinutes,
} from "date-fns";
import {
  convertToLocalTime,
  convertToTimeZone,
  formatToTimeZone,
} from "date-fns-timezone";
import moment from "moment-timezone";
import {
  TIME_FORMAT_MONTH_DAY,
  TIME_FORMAT_MONTH_DAY_YEAR,
} from "@/helpers/constants";
import dayjs from "dayjs";

export const isPreviousYearDate = (date: string | Date) => {
  return new Date(date).getFullYear() < new Date().getFullYear();
};
export const setToEndOfDay = (date: string | Date): Date => {
  const isString = (value: unknown): value is string =>
    typeof value === "string";

  return set(isString(date) ? parseISO(date) : date, {
    hours: 23,
    minutes: 59,
    seconds: 59,
    milliseconds: 999,
  });
};

export const isDateToday = (dateString: string) => {
  const date = new Date(dateString);
  return isToday(date);
};

export const isDateTomorrow = (dateString: string) => {
  const date = new Date(dateString);
  return isTomorrow(date);
};

export const isDateEqual = (date1: string | Date, date2: string | Date) => {
  const dt1 = new Date(date1);
  const dt2 = new Date(date2);
  return isSameDay(dt1, dt2);
};

export const formatDate = (
  dateOrString: Date | string | null | undefined,
  format = TIME_FORMAT_MONTH_DAY,
) => {
  if (dateOrString) {
    const date =
      dateOrString instanceof Date ? dateOrString : new Date(dateOrString);
    const dateFormat =
      format === TIME_FORMAT_MONTH_DAY && isPreviousYearDate(date)
        ? "PP"
        : format;
    // convert date-fns-timezone day of month (D) to date-fns convention (d)
    return dateFnsFormat(date, dateFormat.replace(/D/g, "d"));
  }
  return "";
};

export const formatToClientTimezone = (
  dateOrString: Date | string | null | undefined,
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  format = TIME_FORMAT_MONTH_DAY,
  timezoneFormat = "(z)",
) => {
  let resultDate = "";

  if (dateOrString) {
    const date =
      dateOrString instanceof Date ? dateOrString : new Date(dateOrString);

    const dateFormat =
      format === TIME_FORMAT_MONTH_DAY && isPreviousYearDate(date)
        ? TIME_FORMAT_MONTH_DAY_YEAR
        : format;

    resultDate = formatToTimeZone(date, `${dateFormat} ${timezoneFormat}`, {
      timeZone: timezone,
    });
  }

  return resultDate;
};
export const formatDateForTitle = (
  dateOrString: string | Date,
  timezone: string,
) => {
  return formatToClientTimezone(dateOrString, timezone, "MMM D", "");
};

export const formatTimeForTitle = (
  dateOrString: string | Date,
  timezone: string,
) => {
  return formatToClientTimezone(dateOrString, timezone, "HH:mm", "z");
};

export const formatDateTimeForTitle = (
  dateOrString: string | Date,
  timezone: string,
) => {
  return formatToClientTimezone(
    dateOrString,
    timezone,
    "D MMMM, YYYY HH:mm",
    "z",
  );
};

export const formatDateTimeForCompanyChat = (
  dateOrString: string | Date,
  timezone: string,
  showDate = false,
) => {
  const date =
    dateOrString instanceof Date ? dateOrString : new Date(dateOrString);
  const convertedDate = convertToTimeZone(date, { timeZone: timezone });
  if (!showDate) {
    return `${dateFnsFormat(convertedDate, "p")}`;
  }
  return `${dateFnsFormat(convertedDate, "p")} - ${formatDate(dateOrString)}`;
};

export const getMaxDate = (dateArray: Date[]) => {
  if (dateArray.length === 0) return null;
  return max(dateArray);
};

export function convertTimezoneMoment(
  originalDatetime: Date | string,
  timezone: string,
) {
  return moment(originalDatetime).tz(timezone, true).toDate();
}

export const formatDateToTimezone = (
  dateOrString: Date | string,
  timeZone: string,
) => {
  return convertToTimeZone(dateOrString, { timeZone }).toISOString();
};

export const formatDateToLocalTimezone = (
  dateOrString: Date | string,
  timeZone: string,
) => {
  return convertToLocalTime(dateOrString, { timeZone }).toISOString();
};

export const getEndOfDayForTimezone = (timezone: string) => {
  const now = moment().tz(timezone);
  const endOfDay = now.endOf("day");
  return endOfDay.toDate();
};

export const isDateAfter = (date1: string | Date, date2: string | Date) => {
  const dt1 = new Date(date1);
  const dt2 = new Date(date2);
  return isAfter(dt1, dt2);
};

export const getLaterDate = (date1: Date | string, date2: Date | string) => {
  const date = isDateAfter(date1, date2) ? date1 : date2;
  return formatDate(date, "yyyy-MM-dd'T'HH:mm:ss.SSSSSSxxx");
};

export const getFullDate = (date: Date) => {
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0"); // January is 0!
  const yyyy = date.getFullYear();

  return `${mm}/${dd}/${yyyy}`;
};

export const dateDifference = (
  dateLeft: string | Date,
  dateRight: string | Date,
) => {
  const dt1 = new Date(dateLeft);
  const dt2 = new Date(dateRight);
  return differenceInDays(dt1, dt2);
};

export const timeDifference = (
  firstDate: string | Date,
  secondDate: string | Date,
) => {
  const dt1 = new Date(firstDate);
  const dt2 = new Date(secondDate);
  return differenceInMinutes(dt1, dt2);
};

/**
 * Formats a Date as "h:mm am/pm, X [unit] ago"
 */
export function formatDateRelative(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();

  const minuteMs = 60 * 1000;
  const hourMs = 60 * minuteMs;
  const dayMs = 24 * hourMs;
  const monthMs = 30 * dayMs;
  const yearMs = 365 * dayMs;

  const years = Math.floor(diffMs / yearMs);
  const months = Math.floor(diffMs / monthMs);
  const days = Math.floor(diffMs / dayMs);
  const hours = Math.floor(diffMs / hourMs);
  const minutes = Math.floor(diffMs / minuteMs);

  // time part (e.g. "6:42 pm")
  let h12 = date.getHours() % 12;
  h12 = h12 === 0 ? 12 : h12;
  const mins = String(date.getMinutes()).padStart(2, "0");
  const suffix = date.getHours() < 12 ? "am" : "pm";
  const timePart = `${h12}:${mins} ${suffix}`;

  // relative part
  let relativePart: string;
  if (years >= 1) {
    relativePart = `${years} year${years > 1 ? "s" : ""} ago`;
  } else if (months >= 1) {
    relativePart = `${months} month${months > 1 ? "s" : ""} ago`;
  } else if (days >= 1) {
    relativePart = `${days} day${days > 1 ? "s" : ""} ago`;
  } else if (hours >= 1) {
    relativePart = `${hours} hour${hours > 1 ? "s" : ""} ago`;
  } else if (minutes >= 1) {
    relativePart = `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
  } else {
    relativePart = "just now";
  }

  return `${timePart}, ${relativePart}`;
}

export const formatDateTimeForDelivery = (
  dateOrString: string | Date,
  timezone: string,
) => {
  const date =
    dateOrString instanceof Date ? dateOrString : new Date(dateOrString);
  const convertedDate = convertToTimeZone(date, { timeZone: timezone });
  return `${formatDate(dateOrString)} at ${dateFnsFormat(convertedDate, "p")}`;
};

export function isDateYesterday(date: string) {
  return dayjs(date).isSame(dayjs().subtract(1, "day"), "day");
}
